import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileBottomNav from '../../components/mobile/MobileBottomNav';
import { getProductById } from '../../services/productsApi';
import { useCart } from '../../contexts/CartContext';
import ReactMarkdown from 'react-markdown';
import { generateLiffProductUrl, copyToClipboard } from '../../utils/liffUrlUtils';
import type { Product } from '../../types';
import styles from './MobileProductDetailPage.module.css';

// Strip HTML tags from text (safe - only extracts textContent)
const stripHtml = (html: string | null): string => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const MobileProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const response = await getProductById(id);
        if ('success' in response && response.success) {
          setProduct(response.data);
        } else {
          setError('找不到商品');
        }
      } catch (err) {
        console.error('Failed to fetch product:', err);
        setError(err instanceof Error ? err.message : '載入商品資料失敗');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    if (!product) return;
    setAddingToCart(true);
    setAddToCartMessage(null);
    try {
      await addToCart(product.id, undefined, undefined, quantity);
      setAddToCartMessage('已加入購物車！');
      setTimeout(() => setAddToCartMessage(null), 3000);
    } catch (err) {
      setAddToCartMessage(err instanceof Error ? err.message : '加入購物車失敗');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    setAddingToCart(true);
    setAddToCartMessage(null);
    try {
      await addToCart(product.id, undefined, undefined, quantity);
      navigate('/checkout');
    } catch (err) {
      setAddToCartMessage(err instanceof Error ? err.message : '加入購物車失敗');
      setAddingToCart(false);
    }
  };

  const handleCopyLiffLink = async (viewType: 'compact' | 'tall' | 'full' = 'compact') => {
    if (!product) return;

    const liffUrl = generateLiffProductUrl(product.id, viewType);
    const success = await copyToClipboard(liffUrl);

    if (success) {
      setShareMessage(`已複製 ${viewType === 'compact' ? '精簡' : viewType === 'tall' ? '加高' : '全螢幕'}視圖連結！`);
      setTimeout(() => setShareMessage(null), 3000);
    } else {
      setShareMessage('複製失敗，請手動複製連結');
      setTimeout(() => setShareMessage(null), 3000);
    }
    setShowShareMenu(false);
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '$0';
    return `$${Math.round(price).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className={styles.mobileProductDetailPage}>
        <MobileHeader title="載入中..." />
        <div className={styles.mobileProductDetailPage__loading}>載入中...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={styles.mobileProductDetailPage}>
        <MobileHeader
          title="商品詳情"
          showBack
          onBack={() => navigate('/products')}
        />
        <div className={styles.mobileProductDetailPage__error}>
          <p>{error || '找不到商品'}</p>
          <button onClick={() => navigate('/products')}>返回商品列表</button>
        </div>
      </div>
    );
  }

  const displayPrice = product.salePrice || product.regularPrice;
  const hasSalePrice = product.salePrice !== null && product.regularPrice !== null && product.salePrice < product.regularPrice;

  // Image list - compute with null safety
  const imageUrls = product.imageUrls || [];
  const allImages = product.featuredImageUrl
    ? [product.featuredImageUrl, ...imageUrls.filter(url => url !== product.featuredImageUrl)]
    : imageUrls;

  const showGallery = allImages.length > 0;
  const currentImageUrl = showGallery ? allImages[selectedImageIndex] || '' : '';
  const hasMultipleImages = allImages.length > 1;

  const handlePrevImage = () => {
    setSelectedImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1);
  };

  const handleNextImage = () => {
    setSelectedImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1);
  };

  const maxQuantity = product.stockQuantity > 0 ? Math.min(10, product.stockQuantity) : 10;

  return (
    <div className={styles.mobileProductDetailPage}>
      <MobileHeader
        title="商品詳情"
        showBack
        onBack={() => navigate('/products')}
        semiTransparent
      />

      {/* Image Gallery */}
      {showGallery && (
        <div className={styles.mobileProductDetailPage__gallery}>
          <div className={styles.mobileProductDetailPage__imageWrapper}>
            <img
              src={currentImageUrl}
              alt={product.title}
              className={styles.mobileProductDetailPage__image}
            />
            {hasMultipleImages && (
              <>
                <button
                  className={styles.imageNavBtn}
                  style={{ left: 8 }}
                  onClick={handlePrevImage}
                  type="button"
                >
                  ‹
                </button>
                <button
                  className={styles.imageNavBtn}
                  style={{ right: 8 }}
                  onClick={handleNextImage}
                  type="button"
                >
                  ›
                </button>
                <div className={styles.imageCounter}>
                  {selectedImageIndex + 1} / {allImages.length}
                </div>
              </>
            )}
            {product.stockStatus === 'outofstock' && (
              <div className={styles.soldOutBadge}>已售完</div>
            )}
          </div>
        </div>
      )}

      <div className={styles.mobileProductDetailPage__content}>
        {/* Title */}
        <h1 className={styles.mobileProductDetailPage__title}>{product.title}</h1>

        {/* Category and SKU */}
        <div className={styles.mobileProductDetailPage__meta}>
          <span className={styles.category}>
            📦 {product.productCategory}
          </span>
          {product.sku && (
            <span className={styles.sku}>
              商品編號: {product.sku}
            </span>
          )}

          {/* LIFF Share Button */}
          <button
            className={styles.liffShareBtn}
            onClick={() => setShowShareMenu(!showShareMenu)}
            type="button"
          >
            📱 複製 LINE 連結
          </button>

          {showShareMenu && (
            <div className={styles.liffShareMenu}>
              <button
                onClick={() => handleCopyLiffLink('compact')}
                type="button"
              >
                精簡視圖 (50%)
              </button>
              <button
                onClick={() => handleCopyLiffLink('tall')}
                type="button"
              >
                加高視圖 (80%)
              </button>
              <button
                onClick={() => handleCopyLiffLink('full')}
                type="button"
              >
                全螢幕視圖
              </button>
            </div>
          )}

          {shareMessage && (
            <div className={`${styles.shareMessage} ${shareMessage.includes('失敗') ? styles.shareMessageError : styles.shareMessageSuccess}`}>
              {shareMessage}
            </div>
          )}
        </div>

        {/* Price */}
        {displayPrice && (
          <div className={styles.mobileProductDetailPage__price}>
            {hasSalePrice && product.regularPrice && (
              <span className={styles.originalPrice}>
                {formatPrice(product.regularPrice)}
              </span>
            )}
            <span className={styles.currentPrice}>
              {formatPrice(displayPrice)}
            </span>
            {hasSalePrice && (
              <span className={styles.discountBadge}>
                特價中
              </span>
            )}
          </div>
        )}

        {/* Stock status */}
        <div className={styles.mobileProductDetailPage__stock}>
          {product.stockStatus === 'instock'
            ? product.stockQuantity > 0
              ? `庫存: ${product.stockQuantity} 件`
              : '有庫存'
            : product.stockStatus === 'outofstock'
            ? '已售完'
            : '補貨中'}
        </div>

        {/* Short description */}
        {product.shortDescription && (
          <div className={styles.mobileProductDetailPage__shortDesc}>
            {stripHtml(product.shortDescription)}
          </div>
        )}

        {/* Full description - Render as Markdown */}
        {product.description && (
          <div className={styles.mobileProductDetailPage__section}>
            <h3 className={styles.sectionTitle}>商品描述</h3>
            <div className={styles.description}>
              <ReactMarkdown>{product.description}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Quantity selector */}
        {product.stockStatus !== 'outofstock' && (
          <div className={styles.mobileProductDetailPage__quantity}>
            <label htmlFor="quantity">數量:</label>
            <select
              id="quantity"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className={styles.quantitySelect}
            >
              {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
        )}

        {/* Add to cart message */}
        {addToCartMessage && (
          <div className={`${styles.message} ${addToCartMessage.includes('失敗') ? styles.messageError : styles.messageSuccess}`}>
            {addToCartMessage}
          </div>
        )}

        {/* Specs */}
        {(product.dimensions || product.weight) && (
          <div className={styles.mobileProductDetailPage__section}>
            <h3 className={styles.sectionTitle}>規格</h3>
            {product.dimensions && (
              <div className={styles.specItem}>
                <span>尺寸: {product.dimensions}</span>
              </div>
            )}
            {product.weight && (
              <div className={styles.specItem}>
                <span>重量: {product.weight} kg</span>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {product.tags && product.tags.length > 0 && (
          <div className={styles.mobileProductDetailPage__section}>
            <h3 className={styles.sectionTitle}>標籤</h3>
            <div className={styles.tags}>
              {product.tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* View count */}
        <div className={styles.mobileProductDetailPage__views}>
          瀏覽次數: {product.viewCount}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className={styles.mobileProductDetailPage__actions}>
        {product.stockStatus !== 'outofstock' ? (
          <>
            <button
              className={styles.addToCartBtn}
              onClick={handleAddToCart}
              disabled={addingToCart}
            >
              {addingToCart ? '加入中...' : '加入購物車'}
            </button>
            <button
              className={styles.buyNowBtn}
              onClick={handleBuyNow}
              disabled={addingToCart}
            >
              {addingToCart ? '處理中...' : '立即購買'}
            </button>
          </>
        ) : (
          <div className={styles.outOfStockMsg}>
            此商品已售完，暫時無法購買
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default MobileProductDetailPage;
